import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";

// Human-friendly labels + descriptions for each toggle. Keep the keys in sync
// with backend/routes/organization.py::NOTIFICATION_DEFAULTS.
const PREF_META = [
  {
    key: "critical_alerts",
    label: "Critical alerts",
    desc: "Website down, donation page broken, SSL expired, DNS failure. Recommended — these are the ones that cost donations.",
  },
  {
    key: "broken_link_alerts",
    label: "Broken links & PDFs",
    desc: "Notify me when links or downloadable PDFs on my site stop working.",
  },
  {
    key: "social_alerts",
    label: "Social media links",
    desc: "Notify me when a Facebook, Instagram, LinkedIn, or X link on my site breaks.",
  },
  {
    key: "quality_alerts",
    label: "Website quality issues",
    desc: "Notify me about SEO tags, accessibility, and other quality-tier issues.",
  },
  {
    key: "monthly_report_email",
    label: "Monthly report email",
    desc: "Send my Mission Watch Score by email on the 1st of every month.",
  },
  {
    key: "trial_reminder_emails",
    label: "Trial reminder emails",
    desc: "Remind me 4 days and 24 hours before my free trial converts to a paid plan.",
  },
];


export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/notification-preferences")
      .then(({ data }) => { if (mounted) setPrefs(data); })
      .catch(() => { if (mounted) setPrefs({}); });
    return () => { mounted = false; };
  }, []);

  const toggle = (key) => () => {
    setPrefs((p) => ({ ...(p || {}), [key]: !(p?.[key] ?? true) }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notification-preferences", prefs);
      setPrefs(data);
      setDirty(false);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (prefs === null) {
    return (
      <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="notifications-card">
        <p className="text-sm text-charcoal-400 font-mono">Loading notification preferences…</p>
      </section>
    );
  }
  const allOn = PREF_META.every((m) => prefs[m.key] !== false);
  return (
    <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="notifications-card">
      <div className="flex items-center gap-2 mb-1">
        {allOn ? <Bell className="w-4 h-4 text-brand" /> : <BellOff className="w-4 h-4 text-amber-500" />}
        <h2 className="text-lg font-medium text-charcoal-900">Notifications</h2>
      </div>
      <p className="text-sm text-charcoal-500 mb-5">
        Turn any alert off if it isn't useful. Everything is on by default so you never miss a critical failure.
      </p>

      <div className="space-y-3">
        {PREF_META.map((m) => {
          const on = prefs[m.key] !== false;
          return (
            <div
              key={m.key}
              className="flex items-start justify-between gap-4 py-3 px-3.5 rounded-xl border border-black/5 bg-charcoal-50/50"
              data-testid={`pref-row-${m.key}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-charcoal-800">{m.label}</p>
                <p className="text-xs text-charcoal-500 mt-1 leading-relaxed">{m.desc}</p>
              </div>
              <button
                type="button"
                onClick={toggle(m.key)}
                aria-pressed={on}
                data-testid={`pref-toggle-${m.key}`}
                className={`shrink-0 relative w-11 h-6 rounded-full transition-colors ${on ? "bg-brand" : "bg-charcoal-300"}`}
                title={on ? "On — click to turn off" : "Off — click to turn on"}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0"}`}
                />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <p className="text-xs font-mono text-charcoal-400">
          {dirty ? "Unsaved changes" : "All changes saved"}
        </p>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-xl bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 transition-colors disabled:opacity-60"
          data-testid="save-prefs-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </section>
  );
}
